import {
  Component,
  ContentChild,
  Input,
  OnChanges,
  SimpleChanges,
  TemplateRef,
  TrackByFunction,
} from '@angular/core';

interface AccordionTemplateContext<T> {
  $implicit: T;
  index: number;
  expanded: boolean;
}

@Component({
  selector: 'sigecom-accordion',
  template: `
    <div class="flex flex-col gap-3">
      <div
        *ngFor="let item of items; let index = index; trackBy: trackByFn"
        class="w-full rounded-lg overflow-hidden"
        [ngClass]="containerClass"
      >
        <button
          type="button"
          class="w-full p-4 flex items-center justify-between gap-4 cursor-pointer text-left transition-colors duration-200 hover:brightness-95"
          [ngClass]="headerClass"
          [attr.aria-expanded]="isExpanded(index)"
          [attr.aria-controls]="'sigecom-accordion-panel-' + index"
          (click)="toggleItem(index)"
        >
          <ng-container
            *ngTemplateOutlet="
              headerTemplate || defaultHeader;
              context: getTemplateContext(item, index)
            "
          ></ng-container>

          <fa-icon
            [icon]="isExpanded(index) ? 'chevron-up' : 'chevron-down'"
            class="text-sigecom-dark-gray2 shrink-0"
          />
        </button>

        <div
          *ngIf="isExpanded(index)"
          class="w-full p-4 bg-white flex flex-col gap-3"
          [attr.id]="'sigecom-accordion-panel-' + index"
        >
          <ng-container
            *ngTemplateOutlet="
              contentTemplate || defaultContent;
              context: getTemplateContext(item, index)
            "
          ></ng-container>
        </div>
      </div>
    </div>

    <ng-template #defaultHeader let-item let-index="index">
      <div class="flex items-center gap-3">
        <b class="text-lg text-sigecom-dark-gray2">Item {{ index + 1 }}</b>
      </div>
    </ng-template>

    <ng-template #defaultContent let-item>
      <pre class="whitespace-pre-wrap text-sm text-sigecom-gray">{{
        item | json
      }}</pre>
    </ng-template>
  `,
})
export class AccordionComponent<T> implements OnChanges {
  @Input() items: T[] = [];
  @Input() trackByFn: TrackByFunction<T> = (index: number) => index;
  @Input() headerClass = 'bg-sigecom-item-selected';
  @Input() containerClass = 'border border-gray-200';

  @ContentChild('accordionHeader', { static: false })
  headerTemplate?: TemplateRef<AccordionTemplateContext<T>>;

  @ContentChild('accordionContent', { static: false })
  contentTemplate?: TemplateRef<AccordionTemplateContext<T>>;

  protected expandedItems: boolean[] = [];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['items']) {
      this.expandedItems = new Array(this.items?.length ?? 0).fill(false);
    }
  }

  toggleItem(index: number): void {
    this.expandedItems = this.expandedItems.map((expanded, currentIndex) =>
      currentIndex === index ? !expanded : expanded,
    );
  }

  isExpanded(index: number): boolean {
    return !!this.expandedItems[index];
  }

  getTemplateContext(item: T, index: number): AccordionTemplateContext<T> {
    return {
      $implicit: item,
      index,
      expanded: this.isExpanded(index),
    };
  }
}
